import { Alert, Button, Card ,CardBody, Col, Input, Label, Row } from "reactstrap";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useParams } from "react-router-dom";
import { editUserQuestionnairesQuestions } from "../../graphql/content-management/addUserQuestionnaires";
import { UserQuestionnaires, UserQuestionnairesQuestion } from "../../interfaces/models/UserQuestionnaire";
import { UserQuestionnairesAnswer } from "../../interfaces/models/UserQuestionnaireAnswer";
import { useUserQuestionnaireDetails } from "../../hooks/useUserQuestionnaires";
import { useUserQuestionnairesAnswers } from "../../hooks/useUserQuestionnaireAnswers";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Loading from "../../components/Loading";
import { useMutation } from "@apollo/client";

//#region Interfaces
interface RouteParams {
    id: string;
}
//#endregion Interfaces

const UserQuestionnairesEditorPage = () => {

//#region Initializations
    const { id } = useParams<RouteParams>();

    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Edit Questionnaires', path: `${DASHBOARD_ROOT}/content-management/questionnaires` },
        { name: 'Questionnaire name', path: `${DASHBOARD_ROOT}/content-management/questionnaires/${id}` },
    ];

    const {data, error, loading} = useUserQuestionnaireDetails(id);
    const {data : answerData, error : answerError} = useUserQuestionnairesAnswers();

    const [editUserQuestionnairesHook] = useMutation(editUserQuestionnairesQuestions,{
        onCompleted : () =>{
            toast.success("Questionnaire was successfully updated. Please wait page is Reloading");
        },
        onError : () =>{
            toast.error("Sorry something went wrong on editing Questionnaire");
        }
    });

    const [questionnaire, setQuestionnaire] = useState<UserQuestionnaires>(null);
    const [questions, setQuestions] = useState<UserQuestionnairesQuestion[]>([]);
    const [answers, setAnswers] = useState<UserQuestionnairesAnswer[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        if(data) {
            setQuestionnaire(data.user_questionnaire);
            setQuestions(JSON.parse(JSON.stringify(data.user_questionnaire.questions)));
        }
    },[data]);

    useEffect(() => {
        if(answerData) {
            if(answerData.user_questionnaire_answers) {
                setAnswers(answerData.user_questionnaire_answers);
            }
        }
    },[answerData]);

//#endregion Initializations
//#region Add Functions
    const HandleAddQuestion = () => {
        setQuestions([...questions, { _id : "", answer_id : answers[0]._id, question : "" }]);
    }

    const HandleOnAnswerTypeChange = (index : number, value : string) => {
        const newOptions = [...questions];
        newOptions[index].answer_id = value;
        setQuestions(newOptions);
    };

    const HandleOnQuestionChange = (index : number, value : string) => {
        const newOptions = [...questions];
        newOptions[index].question = value;
        setQuestions(newOptions);
    };

    const HandleRemoveQuestion = (index : number) => {
        let newOptions = [...questions];
        newOptions.splice(index,1);
        setQuestions(newOptions);
    }
    
    const HandleSubmitQuestion = async () => {
        if(questions.length === 0) {
            toast.error("Please add atleast 1 question");
            return;
        }

        if(questions.find(u => u.question?.trim().length === 0)) {
            toast.error("Please fill-up all fields from the questions");
            return;
        }

        setIsSaving(true);

        let isProcessed = await editUserQuestionnairesHook({
            variables : {
                id : questionnaire._id,
                questions : questions
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);

        if(!isProcessed) {
            setIsSaving(false);
            return;
        }

        setTimeout(() => {
            window.location.reload();
        },1000);

    }
//#region Add Functions

    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />
            {
                error &&
                <Alert color="danger">Sorry something went wrong while getting the Questionnaire</Alert>
            }
            {
                questions.length !== 0 && !loading &&
                <div className="d-flex justify-content-end mb-2">
                    <Button  color="primary" onClick={HandleSubmitQuestion} disabled={isSaving} >Save</Button>
                </div>
            }
            
            {
                questions.length === 0 && !loading &&
                <Alert color="info">No Questions added Yet</Alert>
            }
            {
                !questionnaire && loading ?
                    <Loading />
                :
                questions.map((item, key) => 
                    <Card key={key} className="mt-3">
                        <CardBody>
                            <div className="d-flex justify-content-between mb-2">
                                <h4>Question {key + 1}</h4>
                                <Button size="sm" color="danger" onClick={() => { HandleRemoveQuestion(key);}}>Remove</Button>
                            </div>
                            <div>
                                <Input type="textarea" placeholder="Please input question here" value={item.question} onChange={(e) => { HandleOnQuestionChange(key, e.currentTarget.value) }} />
                                {
                                    item.question?.trim().length === 0 &&
                                    <small className="text-warning">Required</small>
                                }
                            </div>
                            <Row className="pt-3">
                                <Col md={6}>
                                <small className="w-100 font-weight-bold">Answer Type </small>
                                    <Input type="select" value={item.answer_id} onChange={(e) => { HandleOnAnswerTypeChange(key, e.currentTarget.value) }} >
                                        {
                                            answerError &&
                                            <option>ERROR</option>   
                                        }
                                        {
                                            answers.map((ans, asnKey) => 
                                                <option key={asnKey} value={ans._id}>{ans.name}</option> 
                                            )
                                        }
                                    </Input>
                                </Col>
                            </Row>
                            <Row className="p-3">
                                {
                                     answers.find(u => u._id === item.answer_id)?.answers.map((opt, optKey) => 
                                        <Col key={optKey}>
                                            <div className="">
                                                <Input type="radio" readOnly name={`question_${key}`}  value={opt._id} className="mt-2" />
                                                <Label >{opt.label}</Label>
                                            </div>
                                        </Col>
                                    )
                                }
                            </Row>
                        </CardBody>
                    </Card>
                )

            }
            {
                !loading && 
                <div className="d-flex justify-content-center mt-4">
                    <Button color="secondary" onClick={HandleAddQuestion}>Add Question</Button>
                </div>
            }
            
        </>
    )
}

export { UserQuestionnairesEditorPage as UserQuestionnairesEditor };